import { useTranslate } from '@hooks/i18n/useTranslate';
import { ActionIcon, Box, Button, Group } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import {
    ArrowCircleUpIcon,
    AtIcon,
    ChartScatterIcon,
    StopCircleIcon,
    XIcon,
} from '@phosphor-icons/react';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import { EntityMentionProvider } from '@providers/EntityMention';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import Mention from '@tiptap/extension-mention';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
} from 'react';

import { AI_MODAL_INPUT_SUBMIT_BUTTON_TEST_ID } from '../constants';
import { generateEntityMentionSuggestion } from './generateEntityMentionSuggestion';
import { convertMentionsToMarkdown, getRichTextEditorStyles } from './utils';

interface ChatInputProps {
    isCompact?: boolean;
}

const ChatInputInner: FC<ChatInputProps> = ({ isCompact = false }) => {
    const { t } = useTranslate();

    const {
        handleSendMessage,
        abortStream,
        clearPushedAiInputText,
        setAiContext,
    } = useAiAnalystContext((context) => context.actions);

    const { pushedInputText, aiContext, isStreaming, aiChatMessages } =
        useAiAnalystContext((context) => context.state);

    const [localInputText, setLocalInputText] = useState('');
    const [isContextButtonHovered, setIsContextButtonHovered] = useState(false);
    const [isSubmitStripHovered, setIsSubmitStripHovered] = useState(false);
    const mentionSuggestionRef = useRef({
        isActive: false,
        isProcessing: false,
    });

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                bulletList: false,
                orderedList: false,
                listItem: false,
            }),
            Mention.extend({
                addAttributes() {
                    return {
                        id: {
                            default: null,
                        },
                        label: {
                            default: null,
                        },
                        type: {
                            default: null,
                        },
                    };
                },
            }).configure({
                HTMLAttributes: {
                    class: 'mention',
                },
                suggestion: generateEntityMentionSuggestion({
                    onStart: () => {
                        mentionSuggestionRef.current.isActive = true;
                        mentionSuggestionRef.current.isProcessing = false;
                    },
                    onExit: () => {
                        mentionSuggestionRef.current.isProcessing = true;
                        setTimeout(() => {
                            mentionSuggestionRef.current.isActive = false;
                            mentionSuggestionRef.current.isProcessing = false;
                        }, 50);
                    },
                }),
            }),
        ],
        content: localInputText,
        editable: !isStreaming,
        onUpdate: ({ editor: updatedEditor }) => {
            setLocalInputText(updatedEditor.getText());
        },
        editorProps: {
            handlePaste: (view, event) => {
                // Get plain text from clipboard
                const text = event.clipboardData?.getData('text/plain');
                if (text) {
                    // Prevent default paste behavior
                    event.preventDefault();
                    // Insert plain text without formatting
                    view.dispatch(
                        view.state.tr.insertText(
                            text,
                            view.state.selection.from,
                        ),
                    );
                    return true;
                }
                return false;
            },
        },
    });

    //Info: Handle editor editable state and focus based on streaming
    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!isStreaming);
        if (!isStreaming) {
            editor.commands.focus();
        }
    }, [editor, isStreaming]);

    //Info: Autofocus on mount when editor is ready
    useEffect(() => {
        if (editor && !isStreaming) {
            const timer = setTimeout(() => {
                editor.commands.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [editor, isStreaming]);

    useEffect(() => {
        if (pushedInputText !== null && editor) {
            editor.commands.setContent(pushedInputText);
            setLocalInputText(pushedInputText);
            clearPushedAiInputText();

            // Focus with a small delay to ensure content is set and editor is ready
            setTimeout(() => {
                if (editor && !isStreaming) {
                    editor.commands.focus();
                    // Move cursor to end of content
                    editor.commands.setTextSelection(
                        editor.state.doc.content.size,
                    );
                }
            }, 100);
        }
    }, [pushedInputText, clearPushedAiInputText, editor, isStreaming]);

    const submitForm = useCallback(async () => {
        const textToSubmit = localInputText.trim();
        if (textToSubmit === '' || !editor) return;
        const processedText = convertMentionsToMarkdown(editor);
        editor.commands.clearContent();
        setLocalInputText('');
        await handleSendMessage({
            content: processedText,
        });
    }, [localInputText, handleSendMessage, editor]);

    const handleSubmit = useCallback(async () => {
        if (isStreaming) {
            abortStream();
            return;
        }

        await submitForm();
    }, [isStreaming, abortStream, submitForm]);

    const handleMentionClick = useCallback(() => {
        if (!editor || isStreaming) return;

        editor.chain().focus();
        const currentContent = editor.getText();
        let contentToInsert = '@';

        if (currentContent !== '') {
            // If there's content but last character is not a space, add a space before @
            const lastChar = currentContent[currentContent.length - 1];
            if (lastChar !== ' ') {
                contentToInsert = ' @';
            }
        }

        // Insert @ character (with space if needed) to trigger mentions
        editor.chain().insertContent(contentToInsert).run();
    }, [editor, isStreaming]);

    const selectedAiContext = useMemo(() => {
        if (!aiContext) return null;
        return {
            context: 'home',
            label: 'PO Context',
            icon: ChartScatterIcon,
            enabled: true,
            placeholder: 'Ask questions about this Purchase Order...',
        };
    }, [aiContext]);

    const handleClearContext = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            setAiContext(null);
            setTimeout(() => {
                setIsContextButtonHovered(false);
            }, 300);
        },
        [setAiContext, setIsContextButtonHovered],
    );

    const hasMessages = aiChatMessages.length;

    const placeholderText = useMemo(() => {
        if (selectedAiContext) {
            return selectedAiContext.placeholder;
        }

        if (hasMessages) {
            return t('chat.messages_placeholder');
        }

        return t('chat.empty_messages_placeholder');
    }, [hasMessages, selectedAiContext, t]);

    const containerClasses = useMemo(() => {
        if (isCompact) {
            return 'flex w-full flex-col rounded-2xl border border-slate-200 bg-white shadow-none';
        }
        return 'flex w-full flex-col rounded-lg bg-white';
    }, [isCompact]);

    return (
        <Box className={containerClasses}>
            <Box className="relative">
                {/* placeholder */}
                {localInputText.trim() === '' && (
                    <Box className="pointer-events-none absolute top-0 left-0 z-10 pt-3 pr-3 pb-2 pl-3.5 text-sm font-medium text-gray-600 select-none">
                        {placeholderText}
                    </Box>
                )}
                <RichTextEditor
                    editor={editor}
                    styles={getRichTextEditorStyles(
                        isStreaming,
                        isSubmitStripHovered,
                    )}
                    style={
                        {
                            // Additional inline styles to force behavior
                            '--tiptap-max-height': isCompact
                                ? '120px'
                                : '188px',
                            '--tiptap-min-height': isCompact ? '60px' : '84px',
                        } as React.CSSProperties
                    }
                    onKeyDown={async (event) => {
                        const { isActive, isProcessing } =
                            mentionSuggestionRef.current;
                        if (
                            event.key === 'Enter' &&
                            !event.shiftKey &&
                            !isActive &&
                            !isProcessing
                        ) {
                            event.preventDefault();
                            await submitForm();
                        }
                    }}
                >
                    <RichTextEditor.Content />
                </RichTextEditor>

                {/* Submit Button Strip - positioned absolutely within the editor */}
                <Group
                    className="absolute right-2 bottom-2 left-2 items-center justify-between"
                    onMouseEnter={() => setIsSubmitStripHovered(true)}
                    onMouseLeave={() => setIsSubmitStripHovered(false)}
                    onClick={(e) => {
                        // Only focus if clicking on the empty area, not on buttons
                        if (e.target === e.currentTarget) {
                            if (editor && !isStreaming) {
                                editor.commands.focus();
                            }
                        }
                    }}
                >
                    <Group className="gap-2">
                        <Box>
                            <ActionIcon
                                onClick={handleMentionClick}
                                className={`border-gray-250 h-[30px] w-fit rounded-lg border !bg-transparent px-2 hover:!bg-gray-100 ${
                                    isStreaming ? 'pointer-events-none' : ''
                                }`}
                                aria-label="Open mentions"
                            >
                                <AtIcon
                                    color="rgb(var(--color-gray-700))"
                                    size={14}
                                    weight="bold"
                                />
                            </ActionIcon>
                        </Box>
                        <AnimatePresence mode="wait">
                            {selectedAiContext && (
                                <motion.div
                                    key="context-button"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{
                                        duration: 0.1,
                                    }}
                                    onClick={handleClearContext}
                                    className={`${
                                        isStreaming ? 'pointer-events-none' : ''
                                    }`}
                                >
                                    <motion.div
                                        animate={{
                                            width: isContextButtonHovered
                                                ? 'auto'
                                                : 'auto',
                                        }}
                                        transition={{
                                            duration: 0.2,
                                        }}
                                        onMouseEnter={() =>
                                            setIsContextButtonHovered(true)
                                        }
                                        onMouseLeave={() =>
                                            setIsContextButtonHovered(false)
                                        }
                                    >
                                        <Button
                                            variant={ButtonVariant.OUTLINED}
                                            className={`box-border !h-[30px] !w-fit overflow-hidden rounded-lg !bg-white/6 p-1 text-sm font-medium hover:!bg-transparent ${
                                                isContextButtonHovered
                                                    ? '!border !border-white/10 !bg-transparent text-gray-600'
                                                    : '!border-none text-gray-800'
                                            }`}
                                        >
                                            <motion.div
                                                className="flex items-center gap-1"
                                                animate={{
                                                    paddingRight: '0px',
                                                }}
                                                transition={{
                                                    duration: 0.2,
                                                }}
                                            >
                                                <selectedAiContext.icon
                                                    size={14}
                                                    color={
                                                        isContextButtonHovered
                                                            ? 'rgb(var(--color-gray-600))'
                                                            : 'rgb(var(--color-gray-800))'
                                                    }
                                                />

                                                <span>
                                                    {selectedAiContext.label}
                                                </span>

                                                <motion.div
                                                    initial={false}
                                                    animate={{
                                                        opacity:
                                                            isContextButtonHovered
                                                                ? 1
                                                                : 0,
                                                        scale: isContextButtonHovered
                                                            ? 1
                                                            : 0.8,
                                                        width: isContextButtonHovered
                                                            ? 'auto'
                                                            : 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.2,
                                                    }}
                                                    className="ml-1 flex cursor-pointer items-center"
                                                    style={{
                                                        marginLeft:
                                                            isContextButtonHovered
                                                                ? '4px'
                                                                : '0px',
                                                    }}
                                                >
                                                    <XIcon
                                                        size={14}
                                                        color={
                                                            'rgb(var(--color-gray-800))'
                                                        }
                                                        weight="bold"
                                                    />
                                                </motion.div>
                                            </motion.div>
                                        </Button>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Group>

                    <Box
                        data-testid={AI_MODAL_INPUT_SUBMIT_BUTTON_TEST_ID}
                        className="relative cursor-pointer rounded-full p-1 hover:bg-white/10"
                        onClick={handleSubmit}
                    >
                        {isStreaming ? (
                            <>
                                <div className="absolute inset-1 h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                <StopCircleIcon
                                    size={28}
                                    color="rgb(var(--color-gray-800))"
                                    weight="fill"
                                    className="relative z-10 animate-pulse"
                                />
                            </>
                        ) : (
                            <ArrowCircleUpIcon
                                size={30}
                                color="rgb(var(--color-gray-800))"
                                weight="fill"
                            />
                        )}
                    </Box>
                </Group>
            </Box>
        </Box>
    );
};

const ChatInput: FC<ChatInputProps> = ({ isCompact }) => {
    return (
        <EntityMentionProvider>
            <ChatInputInner isCompact={isCompact} />
        </EntityMentionProvider>
    );
};

export default React.memo(ChatInput);
